import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, notification } from "antd";
import { Router } from "./router";

import "antd/dist/antd.css";
import "./App.css";

import { Context } from "./context";
//import { TranscodingProvider } from "./TranscodingContext";
import { parseJwt, stripTrailingSlash } from "./utils";
import AdminModule from "./components/AdminModule";

// API base URL is injected at build time via CRA env vars (REACT_APP_*).
// Set REACT_APP_API_URL / REACT_APP_TRANSCODE_URL per environment
// (locally in .env files, on AWS Amplify in Console > Environment variables).
// The fallback keeps local `npm start` working when no env var is set.
const DEFAULT_API_URL = "https://api.minipix.in.net"; // staging default

const apiUrl = stripTrailingSlash(process.env.REACT_APP_API_URL || DEFAULT_API_URL);
const transcodeUrl = stripTrailingSlash(
  process.env.REACT_APP_TRANSCODE_URL || process.env.REACT_APP_API_URL || DEFAULT_API_URL,
);

function buildReelsUrl(path, params) {
  const relativePath = String(path).replace(/^\//, "");
  const url = new URL(relativePath, `${apiUrl}/`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.append(key, value);
      }
    });
  }

  return url.toString();
}

export function formatReelsApiError(data) {
  if (!data || typeof data !== "object") return null;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail
      .map((item) =>
        item && typeof item === "object"
          ? item.msg || item.message || JSON.stringify(item)
          : String(item),
      )
      .filter(Boolean)
      .join("; ");
  }
  if (data.message) return String(data.message);
  return null;
}

export function isReelsMutationSuccess(response) {
  if (!response || typeof response !== "object") return true;
  const success = response.success;
  if (success === undefined || success === null) return true;
  return success === true || success === "True" || success === "true";
}

function serializeReelPayload(payload = {}) {
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  );
}

function assertReelsMutationSuccess(data, fallbackMessage) {
  if (!isReelsMutationSuccess(data)) {
    const error = new Error(
      formatReelsApiError(data) || data?.message || fallbackMessage,
    );
    error.data = data;
    throw error;
  }
  return data;
}

async function reelsRequest(
  path,
  { method = "GET", params, body, signal } = {},
) {
  const token = sessionStorage.getItem("access_token");
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response;

  try {
    response = await fetch(buildReelsUrl(path, params), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (error) {
    if (error?.name === "AbortError" || signal?.aborted) {
      throw error;
    }
    throw error;
  }

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      formatReelsApiError(data) || `HTTP ${response.status}`,
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const getAllReels = async (params = {}, config = {}) => {
  return reelsRequest("reels/cms", {
    params,
    signal: config.signal,
  });
};

export const getSingleReel = async (id) => {
  const data = await reelsRequest(`reels/cms/${id}`);
  return data.reel;
};

export const createReel = async (payload) => {
  const data = await reelsRequest("reels/cms", {
    method: "POST",
    body: serializeReelPayload(payload),
  });
  return assertReelsMutationSuccess(data, "Failed to create reel");
};

export const updateReel = async (id, payload) => {
  const data = await reelsRequest(`reels/cms/${id}`, {
    method: "PUT",
    body: serializeReelPayload(payload),
  });
  return assertReelsMutationSuccess(data, "Failed to update reel");
};

export const deleteReel = async (id) => {
  const data = await reelsRequest(`reels/cms/${id}`, {
    method: "DELETE",
  });
  return assertReelsMutationSuccess(data, "Failed to delete reel");
};

export const getWebseriesDropdown = async () => {
  const data = await reelsRequest("reels/cms/webseries-dropdown");
  return data.webseries || [];
};

export const getReelsBulkUploadUrl = () => `${apiUrl}/reels/cms/bulk-upload`;

const App = () => {
  const [signedIn, setSignedIn] = useState(false);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [superAdmin, setSuperAdmin] = useState(false);
  const [initialTab, setInitialTab] = useState("dashboard");

  const [form] = Form.useForm();

  const contextValue = useMemo(
    () => ({
      token,
      apiUrl,
      transcodeUrl,
      superAdmin,
      getAllReels,
      getSingleReel,
      createReel,
      updateReel,
      deleteReel,
      getWebseriesDropdown,
      getReelsBulkUploadUrl,
      formatReelsApiError,
      isReelsMutationSuccess,
    }),
    [token, superAdmin],
  );

  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (token) {
      setToken(token);
      setSignedIn(true);
    }
    const email = sessionStorage.getItem("user_email");
    if (email) {
      setUserEmail(email);
    }

    const isSuperAdmin = sessionStorage.getItem("superadmin");
    if (isSuperAdmin) {
      isSuperAdmin === "true" ? setSuperAdmin(true) : setSuperAdmin(false);
    }

    const initialTab = sessionStorage.getItem("lastNav");
    if (initialTab) {
      setInitialTab(initialTab);
    }
  }, []);

  const handleLoginSubmit = () => {
    setLoading(true);

    const formData = new FormData();
    formData.append("username", form.getFieldValue("username"));
    formData.append("password", form.getFieldValue("password"));
    formData.append("client_id", "admin");

    fetch(`${apiUrl}/oauth2/token`, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.json();
      })
      .then((result) => {
        sessionStorage.setItem("access_token", result["access_token"]);
        setToken(result["access_token"]);
        setSignedIn(true);
        sessionStorage.setItem("user_email", form.getFieldValue("username"));
        setUserEmail(form.getFieldValue("username"));
        const jwt = parseJwt(result["access_token"]);
        sessionStorage.setItem("superadmin", jwt.superadmin);
        setSuperAdmin(jwt["superadmin"]);
      })
      .catch((error) => {
        if (error.message === "401") {
          notification.error({
            message: "Error",
            description: "Invalid username/password",
          });
        }
        if (error.message === "Failed to fetch") {
          notification.error({
            message: "Network error",
            description:
              "Cannot reach server. Please check API URL or connectivity.",
          });
        }
        console.error("There is a problem with fetch operation: ", error);
      })
      .finally(() => setLoading(false));
  };

  if (signedIn) {
    /*return (
      <Context.Provider value={contextValue}>
        <TranscodingProvider>
          <Router>
            <AdminModule userEmail={userEmail} initialTab={initialTab} />
          </Router>
        </TranscodingProvider>
      </Context.Provider>
    );*/
    return (
      <Context.Provider value={contextValue}>
        <Router>
          <AdminModule userEmail={userEmail} initialTab={initialTab} />
        </Router>
      </Context.Provider>
    );
  } else {
    return (
      <div className="login-form">
        <img src="/images/MiniPix-Logo.png" className="logo" alt="MiniPix" />
        <Form form={form} layout="vertical" onFinish={handleLoginSubmit}>
          <Form.Item label="Username" name="username">
            <Input />
          </Form.Item>
          <Form.Item label="Password" name="password">
            <Input type="password" />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" disabled={loading}>
              Login
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }
};

export default App;
